import React, { useContext, useEffect, useMemo, useState } from 'react';
import SocketContext from 'utils/hooks/useSocket';
import { useWalletData } from 'context/WalletDataContext';
import { useTournament } from 'context/TournamentContext';
import { getTournamentMarginAssets } from 'api/tournamentService';
import { isCash, pairLabelOf } from 'utils/cash';

import {
  PpBackdrop,
  PpSheet,
  PpHandle,
  PpCloseBtn,
  PpSearch,
  PpCats,
  PpList,
  PpRow,
  PpEmpty,
} from './styled';

interface PairPickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (pairAttributes: any) => void;
  activeSym?: string;
  pairsList: { id: number | string; attributes: any }[];
}

const CATEGORIES = ['All', 'My assets'] as const;

const formatPrice = (n: number): string => {
  if (n >= 100) {
    return n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
  }
  return n.toLocaleString('en-US', { maximumFractionDigits: 4, minimumFractionDigits: 2 });
};

const PairPicker = ({
  open,
  onClose,
  onPick,
  activeSym = '',
  pairsList,
}: PairPickerProps) => {
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState<string>('All');
  const { allCoinsValues } = useContext(SocketContext) as any;
  const { walletData } = useWalletData();
  const { tournamentDetails } = useTournament();
  const [marginAssets, setMarginAssets] = useState<string[]>([]);

  const rawData: any = tournamentDetails?.data;
  const tournamentAttrs = rawData?.attributes ?? rawData?.data ?? rawData;
  const tournamentType = tournamentAttrs?.trade_type;
  const tournamentId = rawData?.id;
  const isMargin = tournamentType === 'Margin';

  useEffect(() => {
    if (!open) {
      setQuery('');
      setCat('All');
    }
  }, [open]);

  // Margin tournaments: favorites = backend list of pairs the user is engaged in.
  // Spot tournaments: favorites are derived from walletData (held tickers).
  useEffect(() => {
    if (!open || !isMargin || !tournamentId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await getTournamentMarginAssets(tournamentId);
        if (!cancelled && Array.isArray(list)) {
          setMarginAssets(list.map((s: string) => String(s).toUpperCase()));
        }
      } catch {
        // Soft-fail: My assets tab will simply be empty for this open.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isMargin, tournamentId]);

  const allPairs = useMemo(() => {
    return (pairsList || []).map(({ id, attributes }) => {
      const baseShortName =
        attributes?.base?.data?.attributes?.short_name ??
        attributes?.baseShortName ??
        (typeof attributes?.name === 'string' ? attributes.name.split('/')[0] : undefined) ??
        '?';
      const displayName =
        attributes?.base?.data?.attributes?.name ??
        attributes?.name ??
        baseShortName;
      return {
        id,
        short_name: String(baseShortName).toUpperCase(),
        pair_label: pairLabelOf(String(baseShortName), attributes?.name),
        display_name: displayName,
        raw: attributes,
      };
    });
  }, [pairsList]);

  const favoriteSet = useMemo(() => {
    const set = new Set<string>();
    if (isMargin) {
      marginAssets.forEach((s) => set.add(s));
    } else if (Array.isArray(walletData)) {
      walletData.forEach((w: any) => {
        if (w?.amount > 0 && w?.ticker?.short_name && !isCash(w)) {
          set.add(String(w.ticker.short_name).toUpperCase());
        }
      });
    }
    return set;
  }, [isMargin, marginAssets, walletData]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const filtered = allPairs.filter((p) => {
    if (q) {
      const matches =
        p.short_name.toLowerCase().includes(q) ||
        String(p.display_name || '').toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (cat === 'My assets' && !favoriteSet.has(p.short_name)) {
      return false;
    }
    return true;
  });

  return (
    <PpBackdrop
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <PpCloseBtn type="button" onClick={onClose} aria-label="Close">×</PpCloseBtn>
      <PpSheet>
        <PpHandle />
        <PpSearch>
          <span className="ic" aria-hidden>⌕</span>
          <input
            placeholder="Search pair (BTC, ETH, …)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </PpSearch>
        <PpCats>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              className={'c ' + (c === cat ? 'on' : '')}
              onClick={() => setCat(c)}
            >
              {c}
              {c === 'My assets' && favoriteSet.size > 0 ? ` (${favoriteSet.size})` : ''}
            </button>
          ))}
        </PpCats>
        <PpList>
          {filtered.map((p) => {
            const lower = p.short_name.toLowerCase();
            const data = allCoinsValues?.[lower];
            const change = data?.percentage;
            const last = data?.last;
            const changeClass =
              typeof change === 'number'
                ? change >= 0
                  ? 'up_state'
                  : 'down_state'
                : '';
            const isActive = p.short_name === (activeSym || '').toUpperCase();
            return (
              <PpRow
                key={`${p.id}`}
                $active={isActive}
                onClick={() => {
                  onPick(p.raw);
                  onClose();
                }}
              >
                <div className="ic">
                  <img
                    src={`/icons/tradingPair/use/${p.short_name}.svg`}
                    alt={p.short_name}
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = 'none';
                    }}
                  />
                </div>
                <div className="nm">
                  {p.pair_label}
                  <small>{p.display_name}</small>
                </div>
                <div className="pr">
                  {typeof last === 'number' && Number.isFinite(last) ? `$${formatPrice(last)}` : '—'}
                  {typeof change === 'number' && Number.isFinite(change) && (
                    <small className={changeClass}>
                      {change >= 0 ? '+' : ''}
                      {change.toFixed(2)}%
                    </small>
                  )}
                </div>
              </PpRow>
            );
          })}
          {filtered.length === 0 && (
            <PpEmpty>
              {cat === 'My assets' && favoriteSet.size === 0
                ? 'Your assets will appear here once you make the first transaction.'
                : 'No pairs match.'}
            </PpEmpty>
          )}
        </PpList>
      </PpSheet>
    </PpBackdrop>
  );
};

export default PairPicker;
